import { z } from 'zod';

/**
 * A task is always "for" a user (`userId`), and may optionally point at the
 * client and/or resource it concerns — e.g. "renew SSL cert" linked to both
 * the customer and the firewall resource it protects.
 */

export const TASK_STATES = ['open', 'in_progress', 'done', 'cancelled'] as const;
export type TaskState = (typeof TASK_STATES)[number];

export const TASK_PRIORITIES = ['low', 'normal', 'high', 'urgent'] as const;
export type TaskPriority = (typeof TASK_PRIORITIES)[number];

export const taskSchema = z.object({
  id: z.string(),
  title: z.string().min(1),
  description: z.string().default(''),
  state: z.enum(TASK_STATES).default('open'),
  priority: z.enum(TASK_PRIORITIES).default('normal'),
  /** Who the task is for. */
  userId: z.string().nullable().default(null),
  createdBy: z.string().nullable().default(null),
  /** Client this task relates to, when relevant. */
  customerId: z.string().nullable().default(null),
  /** Resource this task relates to, when relevant. */
  resourceId: z.string().nullable().default(null),
  dueAt: z.number().nullable().default(null),
  completedAt: z.number().nullable().default(null),
  createdAt: z.number(),
  updatedAt: z.number(),
});
export type Task = z.infer<typeof taskSchema>;

export const taskCreateSchema = taskSchema
  .omit({ id: true, createdAt: true, updatedAt: true, completedAt: true, createdBy: true })
  .partial()
  .required({ title: true });
export type TaskCreate = z.infer<typeof taskCreateSchema>;

export const taskUpdateSchema = taskSchema.omit({ id: true, createdAt: true, updatedAt: true, createdBy: true }).partial();
export type TaskUpdate = z.infer<typeof taskUpdateSchema>;
